import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, Mail, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import TemplateSelector from './TemplateSelector';
import ResumePreview from './ResumePreview';
import EmailMeModal from './EmailMeModal';
import {
  uploadAndProcessResume,
  optiraParsedToResumeModel,
  getResumePdfBlob,
  sendResumeEmail,
} from '../lib/utils';
// Processing stages for progress indicator
const PROCESSING_STAGES = [
  { id: 'upload', label: 'Uploading resume', duration: 1000 },
  { id: 'parse', label: 'Parsing content', duration: 3000 },
  { id: 'format', label: 'Formatting data', duration: 1000 },
  { id: 'complete', label: 'Complete', duration: 500 }
];

const CONTACT_REDIRECT_URL = 'https://thirdeyedata.ai/contact-us/';
const GUEST_LIMIT_SESSION_KEY = 'formatresume_guest_limit_session';

const ResumeFormatter = ({ guestStatus, onGuestStatusRefresh, isGuest }) => {
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [file, setFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [resumeModel, setResumeModel] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [limitGraceActive, setLimitGraceActive] = useState(false);
  
  const { toast } = useToast();

  const fileInputRef = useRef(null);
  const pageSessionIdRef = useRef(
    typeof window !== 'undefined' && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const hasGuestLimitExceeded = isGuest && guestStatus && guestStatus.remaining <= 0 && !limitGraceActive;

  const redirectToContactPage = useCallback((description) => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    toast({
      title: 'Free trial ended',
      description: description || 'Redirecting you to our Contact page.',
      variant: 'destructive',
    });

    setTimeout(() => {
      window.location.replace(CONTACT_REDIRECT_URL);
    }, 900);
  }, [isRedirecting, toast]);

  useEffect(() => {
    if (!isGuest || !guestStatus) {
      return;
    }

    if (guestStatus.remaining > 0) {
      setLimitGraceActive(false);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(GUEST_LIMIT_SESSION_KEY);
      }
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const storedSessionId = window.localStorage.getItem(GUEST_LIMIT_SESSION_KEY);
    if (storedSessionId && storedSessionId !== pageSessionIdRef.current) {
      redirectToContactPage('Your 3 free attempts are over. Redirecting now.');
      return;
    }

    if (!storedSessionId) {
      window.localStorage.setItem(GUEST_LIMIT_SESSION_KEY, pageSessionIdRef.current);
    }

    setLimitGraceActive(true);
  }, [isGuest, guestStatus, redirectToContactPage]);

  const handleFileUpload = async (fileOrEvent) => {
    if (hasGuestLimitExceeded) {
      redirectToContactPage('No free attempts left. Redirecting to Contact Us.');
      return;
    }

    // Supports:
    // - input onChange event
    // - File (drag & drop)
    // - FileList / array
    const uploadedFile =
      fileOrEvent instanceof File
        ? fileOrEvent
        : Array.isArray(fileOrEvent)
          ? fileOrEvent[0]
          : fileOrEvent?.target?.files && fileOrEvent.target.files.length
            ? fileOrEvent.target.files[0]
            : null;

    if (!(uploadedFile instanceof File)) {
      toast({
        title: "Invalid file",
        description: "Please select a valid file (PDF, DOCX, or TXT).",
        variant: "destructive",
      });
      return;
    }

    if (uploadedFile) {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];

      if (!validTypes.includes(uploadedFile.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF, Word, or text file.",
          variant: "destructive"
        });
        return;
      }

      setFile(uploadedFile);
      setShowPreview(true);
      setIsProcessing(true);
      setResumeModel(null);
      setProcessingError(null);

      try {
        // Stage 1: Upload
        setProcessingStage('upload');
        setProcessingStage('parse');
        const processResp = await uploadAndProcessResume(uploadedFile);

        // Stage 3: Format
        setProcessingStage('format');
        const p = processResp?.parsed || null;
        
        if (!p) {
          throw new Error('No parsed data received from server');
        }

        const model = optiraParsedToResumeModel(p);
        setResumeModel(model);

        // Stage 4: Complete
        setProcessingStage('complete');
        
        // Small delay to show completion stage
        setTimeout(() => {
          toast({
            title: <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Resume parsed successfully!</span>
            </div>,
            description: 'Your resume is ready. Select a template and download.',
          });
          if (onGuestStatusRefresh) onGuestStatusRefresh();
        }, 500);

      } catch (err) {
        console.error('Processing error:', err);
        setProcessingError(err?.message || 'Unable to process the resume');
        toast({
          title: 'Processing failed',
          description: err?.message || 'Unable to process the resume. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setTimeout(() => {
          setIsProcessing(false);
          setProcessingStage('');
        }, 600);
      }
    }
  };

  const handleTextSubmit = async () => {
    if (hasGuestLimitExceeded) {
      redirectToContactPage('No free attempts left. Redirecting to Contact Us.');
      return;
    }

    const trimmedText = resumeText.trim();
    
    // Basic validation - minimum character count
    if (trimmedText.length < 50) {
      toast({
        title: "Resume text too short",
        description: "Please enter at least 50 characters of resume content.",
        variant: "destructive"
      });
      return;
    }

    // Validate for key resume fields and warn user (but still allow processing)
    const warnings = [];
    // Check for common resume sections
    const hasName = /^[a-z\s]{2,50}/i.test(trimmedText.split('\n')[0]?.trim() || '');
    const hasEmail = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(trimmedText);
    const hasPhone = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/.test(trimmedText);
    const hasExperience = /(experience|work history|employment|professional experience)/i.test(trimmedText);
    const hasEducation = /(education|academic|degree|university|college|school)/i.test(trimmedText);
    const hasSkills = /(skills|technical skills|competencies|proficiencies)/i.test(trimmedText);

    if (!hasName) warnings.push("name/header");
    if (!hasEmail) warnings.push("email");
    if (!hasPhone) warnings.push("phone number");
    if (!hasExperience) warnings.push("experience section");
    if (!hasEducation) warnings.push("education section");
    if (!hasSkills) warnings.push("skills section");

    // Show warning if key fields are missing, but still allow processing
    if (warnings.length > 0) {
      toast({
        title: "Missing Resume Information",
        description: `The following may be missing: ${warnings.join(', ')}. You can still proceed, but the resume may be incomplete.`,
        variant: "default",
      });
    }

    // Create a File object from the pasted text
    const textBlob = new Blob([trimmedText], { type: 'text/plain' });
    const textFile = new File([textBlob], 'pasted_resume.txt', { type: 'text/plain' });
    
    // Set the file state and process through the same pipeline as file upload
    setFile(textFile);
    setShowPreview(true);
    setIsProcessing(true);
    setResumeModel(null);
    setProcessingError(null);

    try {
      // Stage 1: Upload (same as file upload)
      setProcessingStage('upload');
      setProcessingStage('parse');
      const processResp = await uploadAndProcessResume(textFile);

      // Stage 3: Format
      setProcessingStage('format');
      const p = processResp?.parsed || null;
      
      if (!p) {
        throw new Error('No parsed data received from server. The pasted text may not contain sufficient resume information.');
      }

      const model = optiraParsedToResumeModel(p);
      setResumeModel(model);

        // Stage 4: Complete
        setProcessingStage('complete');
      
      // Small delay to show completion stage
        setTimeout(() => {
          toast({
            title: <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Resume created successfully!</span>
            </div>,
            description: 'Your resume is ready. Select a template and download.',
          });
          if (onGuestStatusRefresh) onGuestStatusRefresh();
        }, 500);

    } catch (err) {
      console.error('Processing error:', err);
      const errorMessage = err?.message || 'Unable to process the resume text. Please ensure your text contains resume information like name, experience, education, etc.';
      if (/guest limit reached/i.test(errorMessage)) {
        redirectToContactPage('No free attempts left. Redirecting to Contact Us.');
        return;
      }
      setProcessingError(errorMessage);
      toast({
        title: 'Processing failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingStage('');
      }, 600);
    }
  };

  const handleEmailMyResume = () => {
    if (isGuest && guestStatus && guestStatus.remaining <= 0) {
      if (limitGraceActive) {
        setIsEmailModalOpen(true);
        return;
      }
      redirectToContactPage('Your 3 free attempts are over. Redirecting now.');
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (formData) => {
    setIsSendingEmail(true);

    try {
      setIsExporting(true);
      const pdfBlob = await getResumePdfBlob(resumeModel, selectedTemplate);
      setIsExporting(false);

      await sendResumeEmail({
        name: formData.name,
        companyName: formData.companyName,
        email: formData.email,
        template: selectedTemplate,
        resumeName: resumeModel?.name || '',
        pdfBlob,
        filename: `${resumeModel?.name?.replace(/\s+/g, '_') || 'resume'}_Resume_${selectedTemplate}.pdf`,
      });

      setIsEmailModalOpen(false);

      toast({
        title: 'Resume sent!',
        description: 'Check your inbox — your formatted resume is on its way.',
      });
    } catch (error) {
      setIsExporting(false);
      console.error('Email send failed:', error);
      toast({
        title: 'Unable to send email',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setResumeText('');
    setShowPreview(false);
    setSelectedTemplate('modern');
    setResumeModel(null);
    setProcessingError(null);
    setProcessingStage('');

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Processing progress indicator
  const ProcessingIndicator = () => {
    const currentStageIndex = PROCESSING_STAGES.findIndex(s => s.id === processingStage);
    
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
          <div>
            <p className="font-medium text-slate-900">Processing your resume...</p>
            <p className="text-sm text-slate-600">
              {PROCESSING_STAGES[currentStageIndex]?.label || 'Working on it...'}
            </p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-indigo-100 rounded-full h-2">
          <motion.div
            className="bg-indigo-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ 
              width: `${((currentStageIndex + 1) / PROCESSING_STAGES.length) * 100}%` 
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
        
        {/* Stage indicators */}
        <div className="flex justify-between mt-3">
          {PROCESSING_STAGES.map((stage, index) => (
            <div 
              key={stage.id} 
              className={`text-xs ${
                index <= currentStageIndex 
                  ? 'text-indigo-600 font-medium' 
                  : 'text-slate-400'
              }`}
            >
              {stage.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-xl shadow-xl shadow-indigo-100/50 border border-slate-200 overflow-hidden">
        <div className="p-6 md:p-8">
          {isGuest && guestStatus && (
            <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
              You have {guestStatus.remaining} of {guestStatus.limit} free formats remaining.
            </div>
          )}
          <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-slate-100/80 rounded-lg">
              <TabsTrigger
                value="upload"
                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm transition-all"
              >
                <Upload className="w-4 h-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger
                value="paste"
                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm transition-all"
              >
                <FileText className="w-4 h-4" />
                Paste Text
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt"
                  ref={fileInputRef}
                  onChange={(e) => {
                    handleFileUpload(e);
                    // allow selecting the same file again
                    e.target.value = "";
                  }}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-slate-900">
                      Drop resume here or browse
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      PDF, Word, TXT (Max 10MB)
                    </p>
                  </div>
                </label>
              </div>

              {file && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-sm text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    disabled={isProcessing}
                    className="p-1.5 hover:bg-indigo-100 rounded-md transition-colors text-slate-500 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </TabsContent>

            <TabsContent value="paste" className="space-y-4">
              <div>
                <textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  placeholder="Paste your resume content here...

Example format:
JOHN DOE
Email: john.doe@email.com | Phone: (123) 456-7890

EXPERIENCE
Software Engineer at Tech Corp
2020 - Present
- Developed web applications...

EDUCATION
Bachelor of Science in Computer Science
University Name, 2020

SKILLS
JavaScript, React, Python, etc."
                  disabled={isProcessing}
                  className="w-full h-48 p-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none placeholder:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <Button
                onClick={handleTextSubmit}
                disabled={isProcessing || resumeText.trim().length < 50}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing Resume...
                  </>
                ) : (
                  'Format Resume'
                )}
              </Button>
            </TabsContent>
          </Tabs>

          {/* Processing Indicator */}
          <AnimatePresence>
            {isProcessing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <ProcessingIndicator />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          <AnimatePresence>
            {processingError && !isProcessing && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Processing Error</p>
                  <p className="text-sm text-red-700 mt-1">{processingError}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Message */}
          <AnimatePresence>
            {resumeModel && !isProcessing && !processingError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-start gap-3"
              >
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Resume Ready!</p>
                  <p className="text-sm text-green-700 mt-1">
                    Your resume has been successfully parsed. Choose a template and download.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {(file || (resumeText && showPreview)) && !isProcessing && resumeModel && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-6 pt-6 border-t border-slate-100"
            >
              <h3 className="text-base font-semibold text-slate-900 mb-3">
                Choose Template
              </h3>
              <TemplateSelector
                selectedTemplate={selectedTemplate}
                onSelectTemplate={setSelectedTemplate}
              />

              <div className="mt-6">
                <h3 className="text-base font-semibold text-slate-900 mb-3">
                  Preview & Email
                </h3>
                {/* Visible preview (UI) */}
                <div className="w-full overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/50 p-3 sm:p-4">
                  <div className="min-w-[720px] sm:min-w-0">
                    <ResumePreview
                      template={selectedTemplate}
                      resumeData={resumeModel}
                      loading={false}
                    />
                  </div>
                </div>

                {/* Hidden export-only preview (exact A4 sheet, no UI chrome) */}
                <div style={{ position: "absolute", left: "-99999px", top: 0 }}>
                  <div id="resume-preview">
                    <ResumePreview
                      template={selectedTemplate}
                      resumeData={resumeModel}
                      viewOnly={true}
                      loading={false}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    onClick={handleEmailMyResume}
                    disabled={isExporting || isSendingEmail}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting || isSendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isExporting ? 'Generating PDF...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Email me my resume
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      

      <EmailMeModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        isLoading={isSendingEmail}
        mode="email"
        showCompanyName={false}
        requireName={true}
      />

      {isRedirecting && (
        <div className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 max-w-sm w-[90%] text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900">Redirecting...</h3>
            <p className="text-sm text-slate-600 mt-2">Taking you to the Contact page.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeFormatter;
